import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";

const NUMS = [["min_bet", "Minimum participation (Rs)"], ["max_bet", "Maximum participation (Rs)"],
  ["bonus_percent", "Reward bonus on final prize (%)"], ["offer_rounding", "Round offers to nearest (Rs)"]];

export const DndConfigForm = ({ cfg, onSaved }) => {
  const [f, setF] = useState(cfg);
  const setPrize = (i, v) => setF({ ...f, prizes: f.prizes.map((p, j) => (j === i ? v : p)) });
  const setStage = (i, k, v) => setF({ ...f, stages: f.stages.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const save = async () => {
    const body = {
      enabled: f.enabled, show_remaining_prizes: f.show_remaining_prizes, rules_text: f.rules_text || "",
      prizes: f.prizes.map(Number), stages: f.stages.map((s) => ({ after: Number(s.after), percent: Number(s.percent) })),
    };
    NUMS.forEach(([k]) => { body[k] = Number(f[k]); });
    try { await api.put("/admin/dnd/config", body); toast.success("Deal or No Deal settings saved"); onSaved(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="ec-card p-6 space-y-6" data-testid="dnd-config-form">
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="dnd-enabled-switch" />Game enabled</label>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.show_remaining_prizes} onCheckedChange={(v) => setF({ ...f, show_remaining_prizes: v })} data-testid="dnd-show-remaining-switch" />Show remaining prizes to members</label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {NUMS.map(([k, label]) => (
          <div key={k}><Label className="text-xs">{label}</Label>
            <Input type="number" step="any" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`dnd-cfg-${k}`} /></div>
        ))}
      </div>
      <div>
        <Label className="text-xs">20 box prizes (PKR) — shuffled securely into boxes when each game starts</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          {f.prizes.map((p, i) => (
            <Input key={i} type="number" value={p} onChange={(e) => setPrize(i, e.target.value)} data-testid={`dnd-prize-${i}`} />
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">Banker offer stages (after N opened boxes → % of member's amount)</Label>
        <div className="space-y-2 mt-2">
          {f.stages.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input type="number" value={s.after} onChange={(e) => setStage(i, "after", e.target.value)} className="w-24" data-testid={`dnd-stage-after-${i}`} />
              <span className="text-xs text-gray-500">boxes →</span>
              <Input type="number" step="any" value={s.percent} onChange={(e) => setStage(i, "percent", e.target.value)} className="w-24" data-testid={`dnd-stage-percent-${i}`} />
              <span className="text-xs text-gray-500">%</span>
              <button type="button" onClick={() => setF({ ...f, stages: f.stages.filter((_, j) => j !== i) })} className="text-red-400 ms-auto" data-testid={`dnd-stage-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, stages: [...f.stages, { after: 1, percent: 50 }] })} data-testid="dnd-stage-add"><Plus className="w-4 h-4 me-1" />Add stage</Button>
        </div>
      </div>
      <div><Label className="text-xs">Extra rule text shown to members (optional)</Label>
        <Textarea value={f.rules_text || ""} onChange={(e) => setF({ ...f, rules_text: e.target.value })} className="mt-1" data-testid="dnd-cfg-rules_text" /></div>
      <p className="text-xs text-gray-500">Changes apply to games started afterwards. A game in progress keeps the prizes and stages it started with.</p>
      <Button onClick={save} className="w-full btn-glow font-semibold" data-testid="dnd-save-config">Save Settings</Button>
    </div>
  );
};
